import { Sun, CloudRain, CloudSnow, Zap, Cloud, CloudSun } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import type { WeatherDisplayData } from '@/lib/weather/weather-service';

interface WeatherForecastLineProps {
  weather: WeatherDisplayData;
}

/**
 * Two-color CloudSun component wrapper
 * EXACT COPY from WeatherTimeInfoCard for consistency
 */
const TwoColorCloudSun: React.FC<{ className?: string }> = ({ className = "" }) => {
  // Extract size classes from className
  const sizeMatch = className.match(/h-(\d+)\s+w-(\d+)/);
  const size = sizeMatch ? sizeMatch[1] : '8';

  return (
    <div className="relative inline-flex">
      {/* Base cloud in grey */}
      <CloudSun className={cn(className, "text-gray-400")} />
      {/* Sun overlay in yellow - positioned to only show the sun part */}
      <CloudSun
        className={cn(className, "absolute inset-0 text-yellow-300")}
        style={{
          clipPath: size === '8'
            ? 'polygon(-15% 0%, 100% 0%, 100% 55%, 100% 90%)' // Larger icon - sun is in upper-right corner
            : 'polygon(-15% 0%, 100% 0%, 100% 55%, 100% 90%)' // Smaller icon - sun is in upper-right corner
        }}
      />
    </div>
  );
};

/**
 * Get color for a specific temperature
 * Copied from WeatherTimeInfoCard for consistency
 */
const getTemperatureColor = (temp: number): string => {
  if (temp <= -5) return '#1E40AF'; // blue-800 - deep blue for very cold
  if (temp <= 0) {
    // Interpolate between deep blue and blue
    const ratio = (temp + 5) / 5;
    return `rgb(${30 + (59 - 30) * ratio}, ${64 + (130 - 64) * ratio}, ${175 + (246 - 175) * ratio})`;
  }
  if (temp <= 5) {
    // Interpolate between blue and cyan
    const ratio = temp / 5;
    return `rgb(${59 + (6 - 59) * ratio}, ${130 + (182 - 130) * ratio}, ${246 + (212 - 246) * ratio})`;
  }
  if (temp <= 10) {
    // Interpolate between cyan and light blue/teal
    const ratio = (temp - 5) / 5;
    return `rgb(${6 + (20 - 6) * ratio}, ${182 + (184 - 182) * ratio}, ${212 + (166 - 212) * ratio})`;
  }
  if (temp <= 15) {
    // Green starts here - interpolate between teal and green
    const ratio = (temp - 10) / 5;
    return `rgb(${20 + (16 - 20) * ratio}, ${184 + (185 - 184) * ratio}, ${166 + (129 - 166) * ratio})`;
  }
  if (temp <= 20) {
    // Interpolate between green and light green
    const ratio = (temp - 15) / 5;
    return `rgb(${16 + (132 - 16) * ratio}, ${185 + (204 - 185) * ratio}, ${129 + (9 - 129) * ratio})`;
  }
  if (temp <= 25) {
    // Interpolate between light green and yellow
    const ratio = (temp - 20) / 5;
    return `rgb(${132 + (234 - 132) * ratio}, ${204 + (179 - 204) * ratio}, ${9 + (8 - 9) * ratio})`;
  }
  if (temp <= 30) {
    // Interpolate between yellow and orange
    const ratio = (temp - 25) / 5;
    return `rgb(${234 + (249 - 234) * ratio}, ${179 + (115 - 179) * ratio}, ${8 + (22 - 8) * ratio})`;
  }
  // 30°C and above - solid red
  return '#EF4444'; // red-500
};

/**
 * Generate gradient string for temperature range
 * Copied from WeatherTimeInfoCard for consistency
 */
const generateTemperatureGradient = (lowTemp: number, highTemp: number): string => {
  const steps = 10; // Number of gradient stops
  const gradientStops: string[] = [];

  for (let i = 0; i <= steps; i++) {
    const temp = lowTemp + (highTemp - lowTemp) * (i / steps);
    const color = getTemperatureColor(temp);
    const percentage = (i / steps) * 100;
    gradientStops.push(`${color} ${percentage}%`);
  }

  return `linear-gradient(to right, ${gradientStops.join(', ')})`;
};

/**
 * Render weather icon based on icon identifier
 * Slightly larger icons for better visibility
 */
const renderWeatherIcon = (icon: string): JSX.Element => {
  if (icon === 'sun' || icon === 'clear-night') {
    return <Sun className="h-4 w-4 text-yellow-300" />;
  }
  if (icon === 'cloud') {
    return <TwoColorCloudSun className="h-4 w-4" />;
  }
  if (icon.includes('rain') || icon.includes('drizzle') || icon.includes('showers')) {
    return <CloudRain className="h-4 w-4 text-blue-300" />;
  }
  if (icon === 'snow') {
    return <CloudSnow className="h-4 w-4 text-blue-200" />;
  }
  if (icon === 'storm') {
    return <Zap className="h-4 w-4 text-yellow-300" />;
  }
  return <Cloud className="h-4 w-4 text-gray-300" />;
};

/**
 * Weather forecast line component
 * Displays weather with icon and temperature gradient bar
 * Matches the style of WeatherTimeInfoCard's 4-day forecast
 */
export function WeatherForecastLine({ weather }: WeatherForecastLineProps) {
  const { icon, temperatureMin, temperatureMax } = weather;

  return (
    <div className="flex items-center gap-2 text-xs min-w-[200px] w-full">
      {/* Weather icon */}
      <div className="flex justify-center w-4">
        {renderWeatherIcon(icon)}
      </div>

      {/* Temperature bar and values */}
      <div className="flex-1 flex items-center gap-1">
        <span className="text-muted-foreground w-5 text-right text-xs">
          {temperatureMin}°
        </span>
        <div className="flex-1 relative h-1 bg-muted/20 rounded-full mx-1">
          <div
            className="absolute h-1 rounded-full"
            style={{
              left: '0%',
              width: '100%',
              background: generateTemperatureGradient(temperatureMin, temperatureMax)
            }}
          />
        </div>
        <span className="font-medium w-5 text-xs">
          {temperatureMax}°
        </span>
      </div>
    </div>
  );
}
